import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import PageHeader from "theme/components/common/PageHeader";
import { toast } from "react-toastify";
import apiClient from "api";
import { useIsAdmin } from "state/auth";
import { gettext } from "i18n";
import { REPORT_TYPE_KEY_TO_DISPLAY } from "./MeasurementStationPage";
import { capitalize } from "../utils/formatting";

import "./ReportDetailPage.scss";

interface ReportAttachment {
  uuid: string;
  filename: string;
  mimeType: string;
  url: string | null;
  isAdminOnly?: boolean;
}

interface SensorRow {
  id?: string;
  sensorId: number;
  sensorName: string;
  customSensorName?: string;
  inspected: boolean;
  cleaned: boolean;
  calibrated: boolean;
  replaced: boolean;
  installed?: boolean;
  notes: string;
}

interface ReportDetail {
  uuid: string;
  stationId: number | null;
  stationIdentifier: string | null;
  stationName: string | null;
  organizationName: string | null;
  reportType: string;
  status: "draft" | "finished";
  responsiblePerson: string | null;
  timestamp: string | null;
  createdAt: string;
  updatedAt: string;
  data: {
    addAnnotation?: boolean;
    sensorRows?: SensorRow[];
    batteryVoltageChecked?: boolean;
    batteryChanged?: boolean;
    moistureBagChecked?: boolean;
    dataTransferChecked?: boolean;
    otherNotesPublic?: string;
    otherNotesAdmin?: string;
  };
  attachments: ReportAttachment[];
}

const translateReportType = (reportTypeKey: string): string => {
  const displayName =
    REPORT_TYPE_KEY_TO_DISPLAY[reportTypeKey] ?? reportTypeKey;
  return gettext(displayName);
};

const formatDateTime = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const useReportDetail = (uuid: string) => {
  const [report, setReport] = React.useState<ReportDetail | null>(null);
  const [loading, setLoading] = React.useState(true);

  const fetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.request(
        `/organization-admin/offline-report/detail/${uuid}`,
        { method: "GET" },
      );
      setReport(result as ReportDetail);
    } catch (e) {
      console.error(e);
      toast.error(gettext("Failed to load report"));
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  React.useEffect(() => {
    fetch();
  }, [fetch]);

  return { report, loading, refetch: fetch };
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, value }) => (
  <div className="ReportDetailPage__field">
    <div className="ReportDetailPage__field-label">{label}</div>
    <div className="ReportDetailPage__field-value">{value ?? "—"}</div>
  </div>
);

const CheckField: React.FC<{ label: string; checked: boolean }> = ({
  label,
  checked,
}) => (
  <div className="ReportDetailPage__check">
    <span
      className={`ReportDetailPage__check-icon ReportDetailPage__check-icon--${checked ? "yes" : "no"}`}
    >
      {checked ? "✓" : "✗"}
    </span>
    {label}
  </div>
);

const ReportDetailPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const isGlobalAdmin = useIsAdmin();
  const { report, loading } = useReportDetail(uuid!);
  const [deleting, setDeleting] = React.useState(false);
  const [finalizing, setFinalizing] = React.useState(false);

  const handleEdit = () => {
    navigate(`/admin/reports/${uuid}/edit`);
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        gettext(
          "Delete this report and all its attachments? This cannot be undone.",
        ),
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await apiClient.request(
        `/organization-admin/offline-report/delete/${uuid}`,
        {
          method: "DELETE",
        },
      );
      toast.success(gettext("Report deleted."));
      navigate("/admin/reports");
    } catch (e) {
      console.error(e);
      toast.error(gettext("Failed to delete report."));
      setDeleting(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await apiClient.request(
        `/organization-admin/offline-report/patch/${uuid}`,
        {
          method: "POST",
          data: { status: "finished" },
        },
      );
      toast.success(gettext("Report finalized."));
      navigate("/admin/reports");
    } catch (e) {
      console.error(e);
      toast.error(gettext("Failed to finalize report."));
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="ReportDetailPage">
        <div className="ReportDetailPage__spinner">
          <Spinner animation="border" size="sm" className="me-2" />
          {gettext("Loading…")}
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="ReportDetailPage">
        <div className="ReportDetailPage__empty">
          {gettext("Report not found.")}
        </div>
      </div>
    );
  }

  const {
    addAnnotation,
    sensorRows = [],
    batteryVoltageChecked,
    batteryChanged,
    moistureBagChecked,
    dataTransferChecked,
    otherNotesPublic,
    otherNotesAdmin,
  } = report.data;

  const showStationPerformance = [
    batteryVoltageChecked,
    batteryChanged,
    moistureBagChecked,
    dataTransferChecked,
    otherNotesPublic,
    isGlobalAdmin && otherNotesAdmin,
  ].some(Boolean);

  return (
    <div className="ReportDetailPage">
      <div className="ReportDetailPage__header">
        <div className="ReportDetailPage__header-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate("/admin/reports")}
          >
            {gettext("Back")}
          </Button>
          {isGlobalAdmin && (
            <Button variant="primary" size="sm" onClick={handleEdit}>
              {gettext("Edit Report")}
            </Button>
          )}
          {isGlobalAdmin && report.status === "draft" && (
            <Button
              variant="success"
              size="sm"
              disabled={finalizing}
              onClick={handleFinalize}
            >
              {finalizing ? gettext("Finalizing…") : gettext("Finalize report")}
            </Button>
          )}
          {isGlobalAdmin && (
            <Button
              variant="danger"
              size="sm"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? gettext("Deleting…") : gettext("Delete report")}
            </Button>
          )}
        </div>
        <PageHeader
          title={`${translateReportType(report.reportType)} — ${report.stationName ?? gettext("No station")}`}
        />
      </div>

      <Card className="mb-3">
        <Card.Header>{gettext("General")}</Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6}>
              <Field
                label={gettext("Station")}
                value={`${report.stationName} (ID: ${report.stationIdentifier ?? "-"})`}
              />
              <Field
                label={gettext("Organization")}
                value={report.organizationName}
              />
              <Field
                label={gettext("Report type")}
                value={translateReportType(report.reportType)}
              />
              <Field
                label={gettext("Visit time")}
                value={formatDateTime(report.timestamp)}
              />
              <Field
                label={gettext("Responsible person")}
                value={report.responsiblePerson}
              />
              <Field
                label={gettext("Status")}
                value={
                  <span
                    className={`ReportDetailPage__badge ReportDetailPage__badge--${report.status}`}
                  >
                    {gettext(capitalize(report.status))}
                  </span>
                }
              />
              <Field
                label={gettext("Add annotation to charts")}
                value={
                  <CheckField
                    label={addAnnotation ? gettext("Yes") : gettext("No")}
                    checked={!!addAnnotation}
                  />
                }
              />
            </Col>
            <Col xs={12} md={6}>
              <Field
                label={gettext("Created")}
                value={formatDateTime(report.createdAt)}
              />
              <Field
                label={gettext("Last updated")}
                value={formatDateTime(report.updatedAt)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {sensorRows.length > 0 && (
        <Card className="mb-3">
          <Card.Header>{gettext("Measurements")}</Card.Header>
          <Card.Body>
            {(() => {
              // Calculate instance numbers for duplicate sensors
              const counts: Record<number, number> = {};
              const instanceNumbers: Record<string, number> = {};
              sensorRows.forEach((row, index) => {
                const key = row.id || String(row.sensorId);
                counts[row.sensorId] = (counts[row.sensorId] || 0) + 1;
                instanceNumbers[key] = counts[row.sensorId];
              });
              const hasDuplicates = Object.values(counts).some((c) => c > 1);

              return sensorRows.map((row, index) => {
                const key = row.id || String(row.sensorId);
                const instanceNum = instanceNumbers[key];
                const displayName = row.customSensorName || row.sensorName;
                const showInstanceSuffix =
                  hasDuplicates && counts[row.sensorId] > 1;

                return (
                  <div key={key} className="ReportDetailPage__sensor-row">
                    <div className="ReportDetailPage__sensor-name">
                      {displayName}
                      {showInstanceSuffix && ` (${instanceNum})`}
                    </div>
                    <div className="ReportDetailPage__sensor-checks">
                      {row.inspected && (
                        <CheckField
                          label={gettext("Inspected")}
                          checked={row.inspected}
                        />
                      )}
                      {row.cleaned && (
                        <CheckField
                          label={gettext("Cleaned")}
                          checked={row.cleaned}
                        />
                      )}
                      {row.calibrated && (
                        <CheckField
                          label={gettext("Calibrated")}
                          checked={row.calibrated}
                        />
                      )}
                      {row.replaced && (
                        <CheckField
                          label={gettext("Replaced")}
                          checked={row.replaced}
                        />
                      )}
                      {row.installed && (
                        <CheckField
                          label={gettext("Installed")}
                          checked={row.installed}
                        />
                      )}
                    </div>
                    {row.notes && (
                      <div className="ReportDetailPage__sensor-notes">
                        {row.notes}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </Card.Body>
        </Card>
      )}

      {showStationPerformance && (
        <Card className="mb-3">
          <Card.Header>{gettext("Station Performance")}</Card.Header>
          <Card.Body>
            <Row>
              <Col xs={12} md={6}>
                {batteryVoltageChecked && (
                  <CheckField
                    label={gettext("Battery voltage checked")}
                    checked={true}
                  />
                )}
                {batteryChanged && (
                  <CheckField
                    label={gettext("Battery changed")}
                    checked={true}
                  />
                )}
                {moistureBagChecked && (
                  <CheckField
                    label={gettext("Moisture bag checked")}
                    checked={true}
                  />
                )}
                {dataTransferChecked && (
                  <CheckField
                    label={gettext("Data transfer checked")}
                    checked={true}
                  />
                )}
              </Col>
            </Row>
            {otherNotesPublic && (
              <Field label={gettext("Other notes")} value={otherNotesPublic} />
            )}
            {isGlobalAdmin && otherNotesAdmin && (
              <Field
                label={gettext("Other notes (admin only)")}
                value={otherNotesAdmin}
              />
            )}
          </Card.Body>
        </Card>
      )}

      {report.attachments.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            {gettext("Attachments")} ({report.attachments.length})
            {isGlobalAdmin && report.attachments.some((a) => a.isAdminOnly) && (
              <span className="ReportDetailPage__attachments-admin-note">
                {gettext("(includes admin-only photos)")}
              </span>
            )}
          </Card.Header>
          <Card.Body>
            <div className="ReportDetailPage__attachments">
              {report.attachments.map((att) => (
                <div
                  key={att.uuid}
                  className={`ReportDetailPage__attachment${att.isAdminOnly ? " ReportDetailPage__attachment--admin-only" : ""}`}
                >
                  {att.url && att.mimeType.startsWith("image/") ? (
                    <a href={att.url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={att.url}
                        alt={att.filename}
                        className="ReportDetailPage__attachment-img"
                      />
                    </a>
                  ) : (
                    <a
                      href={att.url ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ReportDetailPage__attachment-file"
                    >
                      {att.filename}
                    </a>
                  )}
                  <div className="ReportDetailPage__attachment-name">
                    {att.filename}
                    {att.isAdminOnly && isGlobalAdmin && (
                      <span className="ReportDetailPage__attachment-admin-badge">
                        {gettext("Admin only")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ReportDetailPage;
